<template>
  <div class="wapers-corner-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderPage :page="currentPageInfo" />
    <div class="order">
      <section
        v-if="metaContent.value?.description"
        class="section order-last"
      >
        <div
          class="description"
          v-html="metaContent.value.description"
        />
      </section>
      <div class="order-first">
        <section
          v-if="userWaperCorners.length && authenticated"
          class="my-cards section"
        >
          <div class="title-line">
            <h2 class="title-strat">Derniers contenus sur vos communautés</h2>
          </div>
          <FilterMain
            :current="filterGame"
            :items="myCardsFilterItems"
            :action="(slug) => (filterGame = slug)"
          />
          <div
            v-if="!!myCardsFiltered.length"
            class="slider"
          >
            <CardSlider :cards="myCardsFiltered" />
          </div>
        </section>
        <SearchResults
          :is-loading="isLoading"
          placeholder-text="Rechercher un contenu"
          @search-update="searchUpdateHandler"
        >
          <template #title>
            <h2 class="title-strat">
              {{
                authenticated && !!userWaperCorners.length
                  ? 'Plus de contenus'
                  : 'Tous les contenus'
              }}
            </h2>
          </template>
          <template #subtitle>
            <FilterSub
              :current="filterSubCategory"
              :items="waperCornersSubcategoriesList"
              :action="(slug: string) => changeFilterTo(slug)"
            />
          </template>
          <template #results>
            <CardContainer
              :items="waperCorners"
              :no-result-text="noContent"
            />
          </template>
        </SearchResults>
        <ThePagination
          :total-pages="waperCornersPages"
          :current-page="waperCornersCurrentPage"
          :go-to="changePageTo"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    to.name = 'le-coin-des-wapers'
    to.path = '/le-coin-des-wapers'

    const loadWaperCorners = useLoadCorners()
    const waperCornersStore = useWaperCornersStore()

    const [waperCornerReturn] = await Promise.all([
      loadWaperCorners(to),
      waperCornersStore.fetchWaperCornersSubcategories(),
    ])

    return waperCornerReturn
  },
})

const waperCornersStore = useWaperCornersStore()

const {
  waperCorners,
  userWaperCorners,
  waperCornersPages,
  waperCornersCurrentPage,
  waperCornersSubcategoriesList,
} = storeToRefs(waperCornersStore)

const link = useLinkPrevNext({
  currentPage: waperCornersCurrentPage,
  pagesCount: waperCornersPages,
})

const arianeRoute = [
  {
    title: 'Tout le feed des players',
    route: '',
  },
]

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: 'Tout le feed des players',
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: 'Feed des players : astuces, avis et captures PS5',
  description:
    'Les contenus des joueurs PlayStation : astuces, avis, captures de jeux et contributions de la communauté We Are PlayStation.',
  ogTitle: 'Tout le feed des players We Are PlayStation',
  ogDescription:
    'Astuces, avis, captures et contributions publiés par les Wapers, les membres de la communauté We Are PlayStation.',
  ogImage: 'https://www.weareplaystation.fr/img/tout-le-coin-des-wapers.jpg',
})

const route = useRoute()

const metaContentStore = useMetaContentStore()

const { authenticated } = useAuthenticated()

const filterGame = ref('all')
const filterSubCategory = ref('all')

const currentPageInfo = {
  title: 'Tout le feed des players',
  image: '/img/tout-le-coin-des-wapers.jpg',
  titleSmall: 'tout le',
  titleBig: 'feed des players',
  description:
    'Découvrez toutes les discussions, avis et décryptages sur les jeux vidéo du moment.',
}

const metaContent = computed(() => {
  return metaContentStore.getTransverseBySlug('le-coin-des-wapers')
})

const myCardsFiltered = computed(() => {
  if (filterGame.value === 'all') {
    return userWaperCorners.value
  }

  return userWaperCorners.value.filter(
    (card) => card.community.slug === filterGame.value,
  )
})

const myCardsFilterItems = computed(() => {
  return (
    userWaperCorners.value
      .map((card) => ({ ...card.community }))
      .filter(
        (item, index, arr) =>
          arr.findIndex((el) => item.slug === el.slug) === index,
      ) || []
  )
})

const searchData = async () => {
  isLoading.value = true

  const filter =
    filterSubCategory.value !== 'all' ? filterSubCategory.value : ''
  await waperCornersStore.fetchWaperCorners({
    page: 1,
    search: search.value,
    filter,
  })

  if (!waperCorners.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent } = useSearching({
  searchData,
})

const loadWapersCorners = useLoadCorners()

const changeFilterTo = async (subCategory: string) => {
  filterSubCategory.value = subCategory
  const filter =
    filterSubCategory.value !== 'all' ? filterSubCategory.value : ''
  await waperCornersStore.fetchWaperCorners({
    page: 1,
    search: search.value,
    filter,
  })
}

watch(authenticated, (newValue) => {
  if (newValue) {
    loadWapersCorners(route)
  }
})

const changePageTo = async (index = 1) => {
  if (index > 0) {
    const filter =
      filterSubCategory.value !== 'all' ? filterSubCategory.value : ''
    await waperCornersStore.fetchWaperCorners({
      page: index,
      search: search.value,
      filter,
    })
  }
}
</script>

<style lang="scss" scoped>
.wapers-corner-page {
  min-height: 100vh;
  padding-bottom: 7rem;

  @media screen and (min-width: 700px) {
    padding-bottom: 10rem;
  }

  .my-cards {
    @media screen and (max-width: 700px) {
      margin-top: 5rem;
      padding-right: 0;
      padding-left: 0;
    }
  }

  .title-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.order {
  display: flex;
  flex-direction: column;

  &-first {
    order: 1;
  }

  &-last {
    order: 2;
    width: 100%;
  }

  .description {
    &:deep(h2) {
      margin-bottom: 1.5rem;
    }

    &:deep(h3) {
      margin-bottom: 1rem;
      font-family: SST;
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 2rem;
      letter-spacing: 0.225rem;
      text-transform: uppercase;
    }

    &:deep(p) {
      margin-bottom: 1rem;
    }

    &:deep(ul) {
      margin-bottom: 1rem;
      padding-left: 1em;
      list-style: disc;
    }
  }
}
</style>
